// M4-A §3 Type curves, EUR & shale production: modified-hyperbolic decline (b > 1 with a terminal exponential dmin),
// local (plot × bench) drainage instead of the field-wide sqrt share, stream split (oil / gas Mcf / NGL / produced
// water — exposed for the gas & market workstream), flowing period → artificial lift, frac hits, disposal curtailment,
// economic limit, and the decline-treadmill selector. Only wells with prod.shale (created ≥ 2000) take this path.
import { modHypRate, modHypEur, COMPLETION, MCF_PER_BOE } from '../../data/m4/rules.js';
import { streamsOf } from '../../data/m4/fields.js';
import { eqDef, fieldDef, stat, isAvailable, defsByCat, eraDollars, yf } from '../defs.js';
import { allWells } from '../indexes.js';
import { installLiftImpl } from '../production.js';
import { notify, spend, clamp } from '../util.js';
import { SHALE_SIM, sh, shOpt, fieldShale, plotsInProspect, resDefOf, wellHash, round } from './shaleUtil.js';

const poolKey = (well) => `${well.fieldId}:${well.plot}:${well.reservoirId}`;
export const liquidsShare = (st) => (st.oil ?? 0) + (st.ngl ?? 0);
const worOf = (fid) => fieldDef(fid)?.shale?.waterOilRatio ?? 0;

// Build well.prod for a fracked shale well (job carries the IP30 and the design).
export function startShaleProduction(state, well, ip30) {
  const r = resDefOf(well.fieldId, well.reservoirId);
  const tc = r?.typeCurve ?? { de1: 0.65, b: 1.1, dmin: COMPLETION.dminDefault, streams: { oil: 1 } };
  const prev = well.prod;
  const [f0, f1] = SHALE_SIM.flowingDays;
  well.status = 'PRODUCING';
  well.prod = {
    qi: round(ip30, 1), di: r?.di ?? 2, b: tc.b, dmin: tc.dmin ?? COMPLETION.dminDefault, startDay: state.day, age: 0, rate: 0,
    cum: prev?.cum ?? 0, needsLift: false, shutIn: false, forcedUntil: 0,
    shale: true, ip30: round(ip30, 1), streams: { oil: tc.streams?.oil ?? 1, gas: tc.streams?.gas ?? 0, ngl: tc.streams?.ngl ?? 0 },
    flowDays: Math.round(f0 + (f1 - f0) * wellHash(state, well, 'flow')), permMult: prev?.permMult ?? 1, hitUntil: 0, hitMult: 1,
    eurEst: Math.round(modHypEur(ip30, r?.di ?? 2, tc.b, tc.dmin ?? COMPLETION.dminDefault)), cumOil: prev?.cumOil ?? 0, cumGasMcf: prev?.cumGasMcf ?? 0, cumNgl: prev?.cumNgl ?? 0,
    refracs: prev?.refracs ?? 0, liftDownUntil: 0,
  };
}

// Type-curve rate at an age (days): the original frac plus any refrac increments (each its own fresh curve).
export function curveRate(p, ageDays = p.age) {
  let q = modHypRate(p.qi, p.di, p.b, p.dmin, ageDays / 365);
  for (const r of p.refracs ? p.rf ?? [] : []) if (ageDays >= r.age) q += modHypRate(r.qi, p.di, p.b, p.dmin, (ageDays - r.age) / 365);
  return q * (p.permMult ?? 1);
}
// Daily boe/d of a shale well before the field cap / prorationing (tickProduction hook).
export function shaleRate(state, well, res) {
  const p = well.prod;
  let q = curveRate(p);
  if ((p.hitUntil ?? 0) > state.day) q *= p.hitMult ?? 1;
  q *= poolFactor(state, well, res);
  q *= liftFactor(state, well, q);
  const fs = state.shale?.fields?.[well.fieldId];
  if (fs && fs.curtail < 1) q *= fs.curtail;
  p.needsLift = p.age >= (p.flowDays ?? 365) && !well.lift;
  return Math.max(0, q);
}
// Local drainage (spec §3): the plot × bench pool = reservoir volume / plots in the prospect; wells in other plots
// never see it. Nothing until half the pool is gone, then a linear taper.
export function poolFactor(state, well, res) {
  const pool = (res?.initial ?? 0) / plotsInProspect(state, well.fieldId);
  if (!(pool > 0)) return 1;
  const used = state.shale?.pools?.[poolKey(well)] ?? 0;
  return clamp((pool - used) / (0.5 * pool), 0, 1);
}
export function liftFactor(state, well, q) {
  const p = well.prod;
  if ((p.liftDownUntil ?? 0) > state.day) return 0;
  const flowing = p.age < (p.flowDays ?? 365);
  if (!well.lift) return flowing ? 1 : SHALE_SIM.unliftedMult;
  if (flowing) return 1;
  const d = eqDef(well.lift.defId);
  const st = p.streams ?? { oil: 1 };
  if (stat(d, 'gasWellsOnly', false) && (st.gas ?? 0) < 0.6) return SHALE_SIM.unliftedMult;
  if (stat(d, 'needsGas', false) && (st.gas ?? 0) < 0.05) return SHALE_SIM.unliftedMult;
  let m = stat(d, 'rateMult', 1);
  if (stat(d, 'gasTolerance', null) != null && (st.gas ?? 0) > stat(d, 'gasTolerance', 1)) m *= SHALE_SIM.gasSlugMult;
  const maxFluid = stat(d, 'maxRateBbld', null);
  if (maxFluid != null && q > 0) {
    const perBoe = stat(d, 'gasWellsOnly', false) ? liquidsShare(st) + worOf(well.fieldId) : liquidsShare(st) + worOf(well.fieldId);
    const cap = perBoe > 0 ? maxFluid / perBoe : Infinity;
    if (q * m > cap) m = cap / q;
  }
  return m;
}

// Split today's boe into streams, book them on the field (state.shale.fields[fid] — read by the gas/market
// workstream) and return the crude barrels that go to field storage.
export function shaleProduced(state, well, q) {
  const fs = fieldShale(state, well.fieldId);
  if (fs.day !== state.day) { fs.day = state.day; fs.oilBbl = 0; fs.gasMcf = 0; fs.nglBbl = 0; fs.waterBbl = 0; fs.boe = 0; }
  const st = well.prod.streams ?? streamsOf(resDefOf(well.fieldId, well.reservoirId));
  const oil = q * (st.oil ?? 0), gas = q * (st.gas ?? 0) * MCF_PER_BOE, ngl = q * (st.ngl ?? 0), water = q * worOf(well.fieldId);
  fs.oilBbl += oil; fs.gasMcf += gas; fs.nglBbl += ngl; fs.waterBbl += water; fs.boe += q;
  const p = well.prod;
  p.cumOil = (p.cumOil ?? 0) + oil; p.cumGasMcf = (p.cumGasMcf ?? 0) + gas; p.cumNgl = (p.cumNgl ?? 0) + ngl;
  p.oilToday = round(oil, 2); p.gasMcfToday = round(gas, 1); p.nglToday = round(ngl, 2); p.waterToday = round(water, 1);
  const S = sh(state);
  S.pools[poolKey(well)] = (S.pools[poolKey(well)] ?? 0) + q;
  return oil;
}
// Streams of one shale well today (for views & the market workstream).
export function shaleWellStreams(state, well) {
  const p = well?.prod;
  if (!p?.shale) return null;
  return { boe: round(p.rate ?? 0, 1), oilBbl: p.oilToday ?? 0, gasMcf: p.gasMcfToday ?? 0, nglBbl: p.nglToday ?? 0, waterBbl: p.waterToday ?? 0, streams: p.streams };
}

// Vertical well into a shale bench (no lateral): qiRange is per 7,500 ft of lateral — a vertical sees ~1,000 ft.
export function shaleVerticalQiMult(state, well, r) {
  return r?.unconventional && r.typeCurve && !well.lateral && yf(state) >= 2000 ? SHALE_SIM.verticalQiMult : 1;
}

// ---- daily upkeep (tickPost) ---------------------------------------------------------------------------------
export function tickShaleWells(state) {
  const auto = state.shale?.auto?.lift ?? true;
  for (const well of allWells(state)) {
    const p = well.prod;
    if (!p?.shale || well.status !== 'PRODUCING') continue;
    if (p.age >= (p.flowDays ?? 365)) {
      if (!well.lift) {
        if (auto && autoLift(state, well)) continue;
        if (!p.liftWarned) { p.liftWarned = true; notify(state, 'problem', { wellId: well.id, fieldId: well.fieldId, problem: 'NEEDS_LIFT', level: 'warn', text: `${well.name} has stopped flowing on its own — install a shale ESP, gas lift or rod lift (output ×${SHALE_SIM.unliftedMult}).` }); }
      } else liftWear(state, well);
    }
    if (p.age > 365 && !p.shutIn && (p.rate ?? 0) < COMPLETION.econLimitBoed && (p.liftDownUntil ?? 0) <= state.day && !(p.forcedUntil > state.day)) {
      const fs = state.shale?.fields?.[well.fieldId];
      if (fs && fs.curtail < 0.999) continue;
      well.status = 'DEPLETED';
      p.rate = 0;
      notify(state, 'problem', { wellId: well.id, fieldId: well.fieldId, problem: 'DEPLETED', text: `${well.name} is below its economic limit (${COMPLETION.econLimitBoed} boe/d) after ${Math.round(p.cum).toLocaleString('en-US')} boe. Refrac it or plug it.` });
    }
  }
}
function liftWear(state, well) {
  const p = well.prod, d = eqDef(well.lift.defId);
  const life = stat(d, 'runLifeDays', null);
  if (!life) return;
  p.liftDay ??= state.day;
  const eff = life / stat(d, 'deviatedRodWearMult', 1);
  if (state.day - p.liftDay < eff) return;
  p.liftDay = state.day;
  p.liftDownUntil = state.day + SHALE_SIM.liftFailDowntime;
  const cost = eraDollars(state, SHALE_SIM.liftWorkoverCost, SHALE_SIM.liftWorkoverBaseYear);
  spend(state, cost, 'opex', false);
  well.cost += cost;
  notify(state, 'problem', { wellId: well.id, fieldId: well.fieldId, problem: 'LIFT_FAILED', text: `${well.name}: the ${d.name} failed after ${Math.round(eff)} days — workover rig on the way (${SHALE_SIM.liftFailDowntime} days).` });
}
// Best available lift for a shale well's fluid rate and gas share.
export function pickLift(state, well) {
  const p = well.prod, st = p.streams ?? { oil: 1 };
  const fluid = (p.rate ?? p.qi) * (liquidsShare(st) + worOf(well.fieldId));
  const avail = (id) => eqDef(id) && isAvailable(eqDef(id), state) ? id : null;
  if ((st.gas ?? 0) >= 0.6) return avail('plunger_lift');
  if (fluid > 700) return avail('esp_high_rate') ?? ((st.gas ?? 0) > 0.05 ? avail('gas_lift_hp') : null) ?? avail('rod_lift_long_stroke') ?? fallbackLift(state);
  return avail('rod_lift_long_stroke') ?? ((st.gas ?? 0) > 0.05 ? avail('gas_lift_hp') : null) ?? avail('esp_high_rate') ?? fallbackLift(state);
}
function fallbackLift(state) {
  const d = defsByCat('lift').filter((x) => isAvailable(x, state) && stat(x, 'perWell', true) && !stat(x, 'tiesUpRig', false) && !stat(x, 'gasWellsOnly', false))
    .sort((a, b) => (b.year ?? 0) - (a.year ?? 0))[0];
  return d?.id ?? null;
}
function autoLift(state, well) {
  const id = pickLift(state, well);
  if (!id) return false;
  const r = installLiftImpl(state, well.id, id);
  if (r.ok) { well.prod.liftDay = state.day; notify(state, 'problem', { wellId: well.id, fieldId: well.fieldId, problem: 'LIFT_INSTALLED', text: `${well.name} quit flowing: ${r.msg}` }); }
  return r.ok;
}

// ---- type curve & treadmill views ----------------------------------------------------------------------------
export function typeCurvePoints(qi, di, b, dmin, { years = 30, permMult = 1 } = {}) {
  const pts = [];
  let cum = 0;
  for (let m = 0; m <= years * 12; m++) {
    const q = modHypRate(qi, di, b, dmin, m / 12) * permMult;
    if (m > 0) cum += modHypRate(qi, di, b, dmin, (m - 0.5) / 12) * permMult * 365.25 / 12;
    if (m % (m < 24 ? 1 : 6) === 0) pts.push({ month: m, rate: round(q, 1), cum: Math.round(cum) });
    if (q < COMPLETION.econLimitBoed) { pts.push({ month: m, rate: round(q, 1), cum: Math.round(cum) }); break; }
  }
  return pts;
}
export const firstYearVolume = (qi, di, b, dmin) => { let v = 0; for (let m = 0; m < 12; m++) v += modHypRate(qi, di, b, dmin, (m + 0.5) / 12) * 365.25 / 12; return v; };

// "Flat production needs N new wells a year": Σ next-year decline of producing shale wells / a new well's year-1 volume.
export function declineTreadmill(state, fieldId = null) {
  let decline = 0, now = 0, n = 0;
  const recent = [];
  for (const w of allWells(state)) {
    const p = w.prod;
    if (!p?.shale || (fieldId && w.fieldId !== fieldId)) continue;
    recent.push(w);
    if (w.status !== 'PRODUCING') continue;
    n++;
    const q0 = curveRate(p, p.age);
    const q1 = curveRate(p, p.age + 365);
    now += p.rate ?? q0;
    decline += (q0 - q1) * 365;
  }
  recent.sort((a, b) => (b.prod.startDay ?? 0) - (a.prod.startDay ?? 0));
  const last = recent.slice(0, 5);
  const yr1 = last.length ? last.reduce((a, w) => a + firstYearVolume(w.prod.ip30 ?? w.prod.qi, w.prod.di, w.prod.b, w.prod.dmin), 0) / last.length : 0;
  return { wells: n, rateBoed: round(now, 1), annualDeclineBoe: Math.round(decline), declinePct: now > 0 ? round(decline / (now * 365), 3) : 0, newWellYr1Boe: Math.round(yr1), wellsPerYear: yr1 > 0 ? round(decline / yr1, 1) : null };
}
export { shOpt };
